import React, { useState } from "react";
import AdminService from "../services/adminService";
import { FiSearch, FiX } from "react-icons/fi";
import ConfirmModal from "./ConfirmModal";
import { useDispatch, useSelector } from "react-redux";
import { refreshDataGuest } from '../store/guestSlice';
import { useTranslation } from 'react-i18next';
import { FaUserPlus, FaSortAlphaDown } from "react-icons/fa";

import GuestCard from "./guestList/GuestCard";
import FamilyCard from "./guestList/FamilyCard";
import StatsBar from "./guestList/StatsBar";
import FilterPill from "./guestList/FilterPill";
import TableNameFilter from "./guestList/TableNameFilter";
import {
    buildMoveMessage,
    buildRemoveMessage,
    buildRsvpMessage,
    buildDeleteMessage,
    buildFamilyTableMoveMessage,
    buildFamilyTableRemoveMessage,
    buildFamilyMemberStatusMessage,
    buildFamilyDeleteMessage,
} from "./guestList/messageBuilders";

const capitalizeWords = (str) =>
    str?.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const hasGroupOrFamily = (g) =>
    g.isFamilyGroup ||
    (g.companions?.registered?.length > 0) ||
    (g.companions?.unregistered > 0);

const translateBackendError = (msg) => {
    const map = {
        'Not enough seats at this table.': 'Nema dovoljno mjesta na ovom stolu.',
        'Not enough seats at the reserved table.': 'Nema dovoljno mjesta na rezerviranom stolu.',
        'Not enough seats at the reserved table for the new members.': 'Nema dovoljno mjesta na stolu za nove članove obitelji.',
        'Not enough seats at the reserved table for the updated group.': 'Nema dovoljno mjesta na stolu za ažuriranu obitelj.',
        'Not enough seats at current table for new companions.': 'Nema dovoljno mjesta za nove pratnje.',
        'Not enough seats at current table for the new group size.': 'Nema dovoljno mjesta za novu veličinu grupe.',
        'Table is locked.': 'Stol je zaključan.',
        'Table is locked': 'Stol je zaključan.',
        'You are already at this table.': 'Već ste na ovom stolu.',
        'You are already at this table': 'Već ste na ovom stolu.',
        'Family is already at this table.': 'Obitelj je već na ovom stolu.',
        'Cancellation limit reached.': 'Dosegnut je limit otkazivanja rezervacije.',
        'Cancellation limit reached': 'Dosegnut je limit otkazivanja rezervacije.',
        'No one is attending — cannot reserve a table.': 'Nitko ne dolazi — stol se ne može rezervirati.',
        'No one is attending — cannot assign a table.': 'Nitko ne dolazi — stol se ne može dodijeliti.',
        'No family members are attending — cannot assign a table.': 'Nijedan član obitelji ne dolazi — stol se ne može dodijeliti.',
        'All members must respond before reserving a table.': 'Svi članovi moraju odgovoriti prije rezervacije stola.',
        'Reservation is locked.': 'Rezervacije su zaključane.',
        'Reservation is locked': 'Rezervacije su zaključane.',
        'Family has no active reservation.': 'Obitelj nema aktivnu rezervaciju.',
        'No active reservation to cancel.': 'Nema aktivne rezervacije za otkazivanje.',
        'Cannot cancel — table is locked.': 'Ne može se otkazati — stol je zaključan.',
        'Family not found.': 'Obitelj nije pronađena.',
        'Guest not found.': 'Gost nije pronađen.',
        'Reserved table not found.': 'Rezervirani stol nije pronađen.',
        'Table not found.': 'Stol nije pronađen.',
        'Transaction failed.': 'Operacija nije uspjela.',
        'Transaction failed': 'Operacija nije uspjela.',
    };
    return map[msg] || msg;
};

const GuestList = ({ guests, tables, setActiveForm, setShowNotification, setNotificationMessage, setErrorResponse }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const wedding = useSelector((state) => state.userStore.wedding);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [pendingAction, setPendingAction] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [rsvpFilter, setRsvpFilter] = useState('all');
    const [tableFilter, setTableFilter] = useState('all');
    const [groupFilter, setGroupFilter] = useState('all');
    const [selectedTableNameFilter, setSelectedTableNameFilter] = useState('');
    const [sortMode, setSortMode] = useState('default'); // 'default' | 'alphabetical'

    const filteredGuests = guests.filter(g => {
        const matchSearch = g.fullName.toLowerCase().includes(searchTerm.toLowerCase());

        const matchRsvp =
            rsvpFilter === 'all' ? true :
                rsvpFilter === 'attending' ? (g.rsvpDate && g.isAttending) :
                    rsvpFilter === 'notAttending' ? (g.rsvpDate && !g.isAttending) :
                        rsvpFilter === 'pending' ? !g.rsvpDate : true;

        const matchTable =
            tableFilter === 'all' ? true :
                tableFilter === 'withTable' ? !!g.reservedTableId :
                    tableFilter === 'noTable' ? !g.reservedTableId : true;

        const hasGroup = hasGroupOrFamily(g);
        const matchGroup =
            groupFilter === 'all' ? true :
                groupFilter === 'withGroup' ? hasGroup :
                    groupFilter === 'noGroup' ? !hasGroup : true;

        const matchTableName = selectedTableNameFilter
            ? (() => {
                const id = g.reservedTableId?._id || g.reservedTableId;
                const table = tables.find(t => t._id === id);
                return table?.name === selectedTableNameFilter;
            })()
            : true;

        return matchSearch && matchRsvp && matchTable && matchGroup && matchTableName;
    });

    const sortedFilteredGuests = [...filteredGuests].sort((a, b) => {
        // Obitelji uvijek prve
        if (a.isFamilyGroup && !b.isFamilyGroup) return -1;
        if (!a.isFamilyGroup && b.isFamilyGroup) return 1;

        // Unutar iste grupe — po izabranom sort modu
        if (sortMode === 'alphabetical') {
            return a.fullName.localeCompare(b.fullName, 'hr');
        }
        return 0; // zadrži originalni redoslijed
    });

    // ── Guest handlers (obični gost) ──────────────────────────────────────────
    const handleGuestTableSelect = (guestId, table) => {
        const guest = guests.find(g => g._id === guestId);
        const msg = table === null
            ? buildRemoveMessage(guest, tables)
            : buildMoveMessage(guest, table, tables);
        setModalMessage(msg);
        setPendingAction({ type: 'guest-move', guestId, table });
        setIsModalOpen(true);
    };

    const handleGuestDelete = (guestId) => {
        const guest = guests.find(g => g._id === guestId);
        setModalMessage(buildDeleteMessage(guest));
        setPendingAction({ type: 'guest-delete', guestId });
        setIsModalOpen(true);
    };

    const handleGuestRsvpChange = (guestId, isAttending) => {
        const guest = guests.find(g => g._id === guestId);
        setModalMessage(buildRsvpMessage(guest, isAttending));
        setPendingAction({ type: 'guest-rsvp', guestId, isAttending });
        setIsModalOpen(true);
    };

    // ── Family handlers ───────────────────────────────────────────────────────
    const handleFamilyTableSelect = (familyId, table) => {
        const family = guests.find(g => g._id === familyId);
        const msg = table === null
            ? buildFamilyTableRemoveMessage(family, tables)
            : buildFamilyTableMoveMessage(family, table, tables);
        setModalMessage(msg);
        setPendingAction({ type: 'family-move', familyId, table });
        setIsModalOpen(true);
    };

    const handleFamilyDelete = (familyId) => {
        const family = guests.find(g => g._id === familyId);
        setModalMessage(buildFamilyDeleteMessage(family));
        setPendingAction({ type: 'family-delete', familyId });
        setIsModalOpen(true);
    };

    const handleFamilyMemberStatusChange = (familyId, memberIndex, attending, memberName) => {
        const family = guests.find(g => g._id === familyId);
        setModalMessage(buildFamilyMemberStatusMessage(family, memberName, attending));
        setPendingAction({ type: 'family-member-status', familyId, memberIndex, attending });
        setIsModalOpen(true);
    };

    // ── Confirm dispatcher ────────────────────────────────────────────────────
    const notifySuccess = (message) => {
        dispatch(refreshDataGuest());
        setNotificationMessage(message);
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 3000);
    };

    const notifyError = (err) => {
        const backendMessage = err?.response?.data?.message;

        if (backendMessage) {
            setErrorResponse(translateBackendError(backendMessage));
            setTimeout(() => setErrorResponse(false), 4000);
        } else {
            setErrorResponse(true);
            setTimeout(() => setErrorResponse(false), 3000);
        }
    };

    const handleConfirm = () => {
        setIsModalOpen(false);
        if (!pendingAction) return;

        const action = pendingAction;
        setPendingAction(null);

        switch (action.type) {
            case 'guest-delete':
                AdminService.deleteGuest(action.guestId)
                    .then(() => notifySuccess(t('guestRemoved') || 'Gost je obrisan.'))
                    .catch(notifyError);
                return;

            case 'guest-move':
                AdminService.updateGuest(
                    action.guestId,
                    { reservedTableId: action.table === null ? 'null' : action.table?._id }
                )
                    .then(() => notifySuccess(t('guestMoved') || 'Gost je premješten.'))
                    .catch(notifyError);
                return;

            case 'guest-rsvp':
                AdminService.updateGuest(action.guestId, { isAttending: action.isAttending })
                    .then(() => notifySuccess('RSVP ažuriran.'))
                    .catch(notifyError);
                return;

            case 'family-delete':
                AdminService.deleteFamily(action.familyId)
                    .then(() => notifySuccess('Obitelj je obrisana.'))
                    .catch(notifyError);
                return;

            case 'family-move':
                AdminService.setFamilyTable(
                    action.familyId,
                    { tableId: action.table === null ? 'null' : action.table?._id }
                )
                    .then(() => notifySuccess('Stol obitelji ažuriran.'))
                    .catch(notifyError);
                return;

            case 'family-member-status':
                AdminService.setFamilyMemberStatus(
                    action.familyId,
                    { memberIndex: action.memberIndex, attending: action.attending }
                )
                    .then(() => notifySuccess('Status člana ažuriran.'))
                    .catch(notifyError);
                return;

            default:
                return;
        }
    };

    const handleCopy = (guest) => {
        const defaultGuestMessage =
            "Pozivamo te na naše vjenčanje! Klikni na link kako bi:\n- vidio/la pozivnicu i detalje vjenčanja\n- potvrdio/la dolazak\n- odabrao/la stol";

        if (guest.isFamilyGroup) {
            // Hardkodirana poruka za obitelj (custom inviteMessage se ignorira)
            const msg = `Pozdrav,\nPozivamo ${capitalizeWords(guest.fullName)} na naše vjenčanje! Otvorite link za pozivnicu, potvrdu dolaska i odabir stola.\n\n🔗 ${window.location.origin}/organization/${guest.weddingId}/${guest.password}`;
            navigator.clipboard.writeText(msg);
            setNotificationMessage(t('copySuccess') || 'Link kopiran!');
            setShowNotification(true);
            setTimeout(() => setShowNotification(false), 3000);
            return;
        }

        // Običan gost — custom ili default
        const bodyMessage = wedding?.inviteMessage || defaultGuestMessage;
        const msg = `Pozdrav ${capitalizeWords(guest.fullName)},\n${bodyMessage}\n\n🔗 ${window.location.origin}/organization/${guest.weddingId}/${guest.password}`;
        navigator.clipboard.writeText(msg);
        setNotificationMessage(t('copySuccess') || 'Link kopiran!');
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 3000);
    };

    const counts = {
        all: guests.length,
        attending: guests.filter(g => g.rsvpDate && g.isAttending).length,
        notAttending: guests.filter(g => g.rsvpDate && !g.isAttending).length,
        pending: guests.filter(g => !g.rsvpDate).length,
        withGroup: guests.filter(hasGroupOrFamily).length,
    };

    const resetAllFilters = () => {
        setRsvpFilter('all');
        setSelectedTableNameFilter('');
        setTableFilter('all');
        setGroupFilter('all');
    };

    const hasActiveFilter = rsvpFilter !== 'all' || tableFilter !== 'all' || groupFilter !== 'all' || selectedTableNameFilter;

    return (
        <div className="flex lg:ml-[300px] lg:mr-2">
            <div className="rounded-md w-full mt-2 pt-14 lg:pt-10 px-2">

                <div className="flex justify-between lg:justify-around items-center mb-4">
                    <h1 className="text-3xl font-semibold">{t('guestListTitle') || 'Gosti'}</h1>
                    <button
                        className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 shadow-lg shadow-rose-500/30 transition-all duration-300 text-white p-2 rounded-xl"
                        onClick={() => setActiveForm("addGuest")}
                    >
                        <FaUserPlus className="text-xl" />
                    </button>
                </div>

                {guests.length === 0 ? (
                    <div className="w-full text-center text-gray-500 text-lg py-10">
                        {t('noGuestsAdded') || 'Nema dodanih gostiju.'}
                    </div>
                ) : (
                    <>
                        <StatsBar guests={guests} />

                        <div className="relative mb-3">
                            <input
                                type="text"
                                placeholder={t('searchGuests') || 'Pretraži goste...'}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 pl-10 text-sm focus:outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-200/40 transition"
                            />
                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    <FiX />
                                </button>
                            )}
                        </div>

                        <div className="flex gap-2 flex-wrap mb-3 overflow-x-auto pb-1">
                            <FilterPill
                                label={`Svi (${counts.all})`}
                                active={!hasActiveFilter}
                                onClick={resetAllFilters}
                            />
                            <FilterPill
                                label={`✓ Dolaze (${counts.attending})`}
                                active={rsvpFilter === 'attending'}
                                onClick={() => { setRsvpFilter('attending'); setSelectedTableNameFilter(''); }}
                            />
                            <FilterPill
                                label={`✗ Ne dolaze (${counts.notAttending})`}
                                active={rsvpFilter === 'notAttending'}
                                onClick={() => { setRsvpFilter('notAttending'); setSelectedTableNameFilter(''); }}
                            />
                            <FilterPill
                                label={`? Čekaju (${counts.pending})`}
                                active={rsvpFilter === 'pending'}
                                onClick={() => { setRsvpFilter('pending'); setSelectedTableNameFilter(''); }}
                            />
                            <FilterPill
                                label="Ima stol"
                                active={tableFilter === 'withTable'}
                                onClick={() => { setTableFilter(f => f === 'withTable' ? 'all' : 'withTable'); setSelectedTableNameFilter(''); }}
                            />
                            <FilterPill
                                label="Bez stola"
                                active={tableFilter === 'noTable'}
                                onClick={() => { setTableFilter(f => f === 'noTable' ? 'all' : 'noTable'); setSelectedTableNameFilter(''); }}
                            />
                            <FilterPill
                                label={`👥 Grupe (${counts.withGroup})`}
                                active={groupFilter === 'withGroup'}
                                onClick={() => { setGroupFilter(f => f === 'withGroup' ? 'all' : 'withGroup'); setSelectedTableNameFilter(''); }}
                            />
                            <FilterPill
                                label="Solo"
                                active={groupFilter === 'noGroup'}
                                onClick={() => { setGroupFilter(f => f === 'noGroup' ? 'all' : 'noGroup'); setSelectedTableNameFilter(''); }}
                            />
                            <TableNameFilter
                                tables={tables}
                                selectedTableNameFilter={selectedTableNameFilter}
                                setSelectedTableNameFilter={setSelectedTableNameFilter}
                                setRsvpFilter={setRsvpFilter}
                                setTableFilter={setTableFilter}
                            />
                        </div>

                        <div className="flex items-center justify-between mb-3 px-1">
                            <p className="text-sm text-gray-400">
                                {sortedFilteredGuests.length === guests.length
                                    ? `${guests.length} ${guests.length === 1 ? 'gost' : guests.length < 5 ? 'gosta' : 'gostiju'}`
                                    : `${sortedFilteredGuests.length} od ${guests.length} gostiju`}
                            </p>
                            <button
                                onClick={() => setSortMode(m => m === 'alphabetical' ? 'default' : 'alphabetical')}
                                className={`text-sm font-medium px-3 py-1 rounded-full border transition ${sortMode === 'alphabetical'
                                    ? 'bg-gray-800 text-white border-gray-800'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                                    }`}
                            >
                                <FaSortAlphaDown className="inline mr-1 text-xs" />
                                {sortMode === 'alphabetical' ? 'A-Ž' : 'Abecedno'}
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 w-full items-start pb-10">
                            {sortedFilteredGuests.length > 0 ? (
                                sortedFilteredGuests.map(entity =>
                                    entity.isFamilyGroup ? (
                                        <FamilyCard
                                            key={entity._id}
                                            family={entity}
                                            tables={tables}
                                            onTableSelect={handleFamilyTableSelect}
                                            onDelete={handleFamilyDelete}
                                            onCopy={handleCopy}
                                            onMemberStatusChange={handleFamilyMemberStatusChange}
                                        />
                                    ) : (
                                        <GuestCard
                                            key={entity._id}
                                            guest={entity}
                                            tables={tables}
                                            onTableSelect={handleGuestTableSelect}
                                            onDelete={handleGuestDelete}
                                            onCopy={handleCopy}
                                            onRsvpChange={handleGuestRsvpChange}
                                        />
                                    )
                                )
                            ) : (
                                <div className="col-span-full text-center text-gray-400 py-10 text-sm">
                                    {t('noGuestsFound') || 'Nema rezultata za odabrane filtere.'}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            <ConfirmModal
                isOpen={isModalOpen}
                message={modalMessage}
                onConfirm={handleConfirm}
                onRequestClose={() => { setIsModalOpen(false); setPendingAction(null); }}
            />
        </div>
    );
};

export default GuestList;
